/* eslint-disable react/no-multi-comp */
import React, {
    createContext,
    useContext,
    useEffect,
    useReducer,
    createElement,
    Children,
} from 'react';
import PropTypes from 'prop-types';
import { mapActions, select } from '../util';

// Create the Store context
const StoreContext = createContext(null);

/**
 * @template T
 * @template K
 * @param {Function | Array<string> | string} mapStateToProps A function mapping of store state to prop values, or an array/CSV of properties to map.
 * @param {Object} [actions] Action functions (pure state mappings), or a factory returning them. Every action function gets current state as the first parameter and any other params next
 * @returns {Function} ConnectedComponent
 * @example
 *    const Foo = connect('foo,bar')( ({ foo, bar }) => <div /> )
 * @example
 *    const actions = { someAction }
 *    const Foo = connect('foo,bar', actions)( ({ foo, bar, someAction }) => <div /> )
 * @example
 *    @connect( state => ({ foo: state.foo, bar: state.bar }) )
 *    export class Foo { render({ foo, bar }) { } }
 */
export function connect(mapStateToProps, actions) {
    if (typeof mapStateToProps !== 'function') {
        mapStateToProps = select(mapStateToProps || []);
    }

    /**
     * @param {T} props
     * @returns {JSX.Element}
     */
    return Child => {
        return function Wrapper(props) {
            const store = useContext(StoreContext);
            const [, forceUpdate] = useReducer(x => x + 1, 0);
            let state = mapStateToProps(store ? store.getState() : {}, props);
            const boundActions = actions
                ? mapActions(actions, store)
                : { store };

            const update = () => {
                let mapped = mapStateToProps(
                    store ? store.getState() : {},
                    props,
                );
                for (let i in mapped)
                    if (mapped[i] !== state[i]) {
                        state = mapped;
                        forceUpdate();
                    }
                for (let i in state)
                    if (!(i in mapped)) {
                        state = mapped;
                        forceUpdate();
                    }
            };

            useEffect(() => {
                store.subscribe(update);
                update();
                return () => {
                    store.unsubscribe(update);
                };
            }, [store, props]);

            return createElement(Child, {
                ...boundActions,
                ...props,
                ...state,
            });
        };
    };
}

/**
 * Provider exposes a store (passed as `props.store`) into context.
 *
 * Generally, an entire application is wrapped in a single `<Provider>` at the root.
 * @param {{store: object, children: React.ReactNode}} props
 */
const Provider = ({ store, children }) => (
    <StoreContext.Provider value={store}>
        {Children.only(children)}
    </StoreContext.Provider>
);

Provider.propTypes = {
    store: PropTypes.object.isRequired,
    children: PropTypes.node.isRequired,
};

export { Provider };
